/**
 * Strategic Generation Orchestrator
 * ---------------------------------------------------------------
 * Builds the next behavioral generation strategy by fusing:
 *   - live platform telemetry      (platform_signal_ingestion)
 *   - behavioral drift             (behavioral_platform_drift)
 *   - prediction accuracy          (asset_predictions vs telemetry)
 *   - adaptive memory              (adaptive_learning_profiles)
 *   - generation saturation        (content_assets recent volume)
 *
 * Output: a `generation_strategy_cycles` row + a set of
 * `generation_strategy_actions` directives describing what should
 * be generated next and at what weight.
 */
import { supabase } from "@/integrations/supabase/client";

export const STRATEGY_PROPOSED_EVENT = "greyrock:strategy-proposed";
export const STRATEGY_ACCEPTED_EVENT = "greyrock:strategy-accepted";

export interface StrategyAction {
  id?: string;
  action_type: "increase" | "decrease" | "favor" | "suppress";
  dimension: "archetype" | "format" | "escalation_path" | "pacing" | "platform";
  target: string;
  adjustment: number; // -1..1 magnitude
  confidence: number; // 0..100
  reasoning: string;
}

export interface StrategyCycle {
  id?: string;
  cycle_number: number;
  dominant_platform: string | null;
  dominant_archetype: string | null;
  dominant_format: string | null;
  dominant_escalation: string | null;
  dominant_pacing: string | null;
  saturation_risk: number;
  strategic_priority: string;
  confidence: number;
  projected_virality: number;
  projected_replay_rate: number;
  status: "proposed" | "accepted" | "partial" | "rejected";
  rationale: string;
  locks: Record<string, string[]>;
  actions: StrategyAction[];
}

const avg = (a: number[]) => (a.length ? a.reduce((s, n) => s + n, 0) / a.length : 0);
const top = <T extends string>(rec: Record<T, number>): T | null => {
  const e = Object.entries(rec) as [T, number][];
  if (e.length === 0) return null;
  e.sort((a, b) => b[1] - a[1]);
  return e[0][0];
};

/**
 * Build (but do not persist) the next strategy cycle proposal.
 */
export async function proposeStrategy(locks: Record<string, string[]> = {}): Promise<StrategyCycle> {
  const [{ data: tele }, { data: drift }, { data: assets }, { data: profiles }] = await Promise.all([
    (supabase as any)
      .from("platform_signal_ingestion")
      .select("platform, archetype, format, escalation_path, pacing, virality_score, replay_rate, save_rate, prediction_accuracy, collected_at")
      .order("collected_at", { ascending: false })
      .limit(500),
    (supabase as any)
      .from("behavioral_platform_drift")
      .select("platform, behavioral_pattern, trend_direction, confidence, signal_strength, delta, detected_at, metadata")
      .order("detected_at", { ascending: false })
      .limit(80),
    (supabase as any)
      .from("content_assets")
      .select("archetype, format, platform, escalation_path, behavioral_intensity, created_at")
      .order("created_at", { ascending: false })
      .limit(300),
    (supabase as any)
      .from("adaptive_learning_profiles")
      .select("dimension, key, weight, trend_delta, sample_size")
      .limit(400),
  ]);

  const telemetry = (tele ?? []) as any[];
  const driftRows = (drift ?? []) as any[];
  const recent = (assets ?? []) as any[];
  const memory = (profiles ?? []) as any[];

  // ── Saturation: % of last 100 assets dominated by single key ──
  const last = recent.slice(0, 100);
  const fmtCounts: Record<string, number> = {};
  const archCounts: Record<string, number> = {};
  last.forEach((a) => {
    if (a.format) fmtCounts[a.format] = (fmtCounts[a.format] ?? 0) + 1;
    if (a.archetype) archCounts[a.archetype] = (archCounts[a.archetype] ?? 0) + 1;
  });
  const dominantFmt = top(fmtCounts);
  const dominantArch = top(archCounts);
  const fmtSat = dominantFmt ? (fmtCounts[dominantFmt] / Math.max(1, last.length)) * 100 : 0;
  const archSat = dominantArch ? (archCounts[dominantArch] / Math.max(1, last.length)) * 100 : 0;
  const saturationRisk = Math.round(Math.max(fmtSat, archSat));

  // ── Telemetry by platform ──
  type Bucket = { virality: number[]; replay: number[]; save: number[]; accuracy: number[]; archetype: Record<string, number>; format: Record<string, number>; escalation: Record<string, number>; pacing: Record<string, number> };
  const buckets = new Map<string, Bucket>();
  telemetry.forEach((t) => {
    if (!buckets.has(t.platform)) {
      buckets.set(t.platform, { virality: [], replay: [], save: [], accuracy: [], archetype: {}, format: {}, escalation: {}, pacing: {} });
    }
    const b = buckets.get(t.platform)!;
    b.virality.push(Number(t.virality_score) || 0);
    b.replay.push(Number(t.replay_rate) || 0);
    b.save.push(Number(t.save_rate) || 0);
    if (t.prediction_accuracy != null) b.accuracy.push(Number(t.prediction_accuracy));
    if (t.archetype) b.archetype[t.archetype] = (b.archetype[t.archetype] ?? 0) + Number(t.virality_score || 0);
    if (t.format) b.format[t.format] = (b.format[t.format] ?? 0) + Number(t.virality_score || 0);
    if (t.escalation_path) b.escalation[t.escalation_path] = (b.escalation[t.escalation_path] ?? 0) + Number(t.virality_score || 0);
    if (t.pacing) b.pacing[t.pacing] = (b.pacing[t.pacing] ?? 0) + Number(t.virality_score || 0);
  });

  // Dominant platform = highest avg virality
  let dominantPlatform: string | null = null;
  let bestVir = -1;
  buckets.forEach((b, p) => {
    const v = avg(b.virality);
    if (v > bestVir) { bestVir = v; dominantPlatform = p; }
  });

  const winnerBucket = dominantPlatform ? buckets.get(dominantPlatform)! : null;
  const winningArch = winnerBucket ? top(winnerBucket.archetype) : null;
  const winningFmt = winnerBucket ? top(winnerBucket.format) : null;
  const winningEsc = winnerBucket ? top(winnerBucket.escalation) : null;
  const winningPace = winnerBucket ? top(winnerBucket.pacing) : null;

  // Memory map for confidence boost
  const memMap = new Map<string, { weight: number; sample: number; trend: number }>();
  memory.forEach((m) => memMap.set(`${m.dimension}::${m.key}`, { weight: Number(m.weight) || 1, sample: m.sample_size, trend: Number(m.trend_delta) || 0 }));

  // ── Build directives ──
  const actions: StrategyAction[] = [];

  const addAction = (a: StrategyAction) => {
    const lockedTargets = locks[a.dimension] ?? [];
    if (lockedTargets.includes(a.target)) return; // operator locked → skip
    actions.push(a);
  };

  // 1. Boost winning archetype on dominant platform
  if (winningArch && dominantPlatform) {
    const m = memMap.get(`archetype::${winningArch}`);
    addAction({
      action_type: "increase",
      dimension: "archetype",
      target: winningArch,
      adjustment: +0.18,
      confidence: Math.min(95, 60 + (winnerBucket ? winnerBucket.virality.length * 1.2 : 0) + (m ? m.sample / 5 : 0)),
      reasoning: `${winningArch} is leading virality on ${dominantPlatform} (avg ${Math.round(bestVir)}).`,
    });
  }

  // 2. Boost winning format
  if (winningFmt) {
    addAction({
      action_type: "favor",
      dimension: "format",
      target: winningFmt,
      adjustment: +0.16,
      confidence: 72,
      reasoning: `${winningFmt} format is converting strongest in current telemetry window.`,
    });
  }

  // 3. Drift-driven directives
  driftRows.slice(0, 6).forEach((d) => {
    const arch = d.metadata?.archetype as string | undefined;
    if (!arch) return;
    const rising = d.trend_direction === "rising";
    addAction({
      action_type: rising ? "increase" : "decrease",
      dimension: "archetype",
      target: arch,
      adjustment: rising ? +0.12 : -0.12,
      confidence: Math.round(d.confidence ?? 60),
      reasoning: `${rising ? "Rising" : "Declining"} ${d.behavioral_pattern} on ${d.platform} (Δ ${d.delta}).`,
    });
  });

  // 4. Saturation guard
  if (saturationRisk > 55 && dominantArch) {
    addAction({
      action_type: "suppress",
      dimension: "archetype",
      target: dominantArch,
      adjustment: -0.20,
      confidence: 80,
      reasoning: `Behavioral saturation risk ${saturationRisk}% — over-indexing on ${dominantArch}. Diversifying.`,
    });
  }
  if (saturationRisk > 55 && dominantFmt) {
    addAction({
      action_type: "suppress",
      dimension: "format",
      target: dominantFmt,
      adjustment: -0.18,
      confidence: 78,
      reasoning: `Format ${dominantFmt} dominates ${Math.round(fmtSat)}% of recent batches.`,
    });
  }

  // 5. Escalation / pacing tilt
  if (winningEsc) {
    addAction({
      action_type: "favor",
      dimension: "escalation_path",
      target: winningEsc,
      adjustment: +0.14,
      confidence: 68,
      reasoning: `${winningEsc} escalation produces strongest replay+save behavior on ${dominantPlatform ?? "platforms"}.`,
    });
  }
  if (winningPace) {
    addAction({
      action_type: "favor",
      dimension: "pacing",
      target: winningPace,
      adjustment: +0.10,
      confidence: 64,
      reasoning: `${winningPace} pacing aligns with platform tolerance window.`,
    });
  }

  // ── Cycle level metrics ──
  const allVir = telemetry.map((t) => Number(t.virality_score) || 0);
  const allReplay = telemetry.map((t) => Number(t.replay_rate) || 0);
  const projectedVir = Math.round(Math.min(99, avg(allVir) * 0.6 + 25 + actions.reduce((s, a) => s + a.adjustment * 8, 0)));
  const projectedReplay = Math.round(Math.min(99, avg(allReplay) * 0.7 + 12));
  const overallConfidence = Math.round(actions.length === 0 ? 35 : avg(actions.map((a) => a.confidence)));

  let priority: string;
  if (saturationRisk > 65) priority = "Diversify · saturation risk elevated";
  else if (winningArch && winningFmt && dominantPlatform) priority = `Compound ${winningArch} × ${winningFmt} on ${dominantPlatform}`;
  else if (driftRows.length > 0) priority = "Adapt to platform drift";
  else priority = "Expand behavioral coverage";

  // Cycle number
  const { data: lastCycle } = await (supabase as any)
    .from("generation_strategy_cycles")
    .select("cycle_number")
    .order("cycle_number", { ascending: false })
    .limit(1);
  const nextNum = ((lastCycle?.[0]?.cycle_number as number | undefined) ?? 0) + 1;

  return {
    cycle_number: nextNum,
    dominant_platform: dominantPlatform,
    dominant_archetype: winningArch ?? dominantArch,
    dominant_format: winningFmt ?? dominantFmt,
    dominant_escalation: winningEsc,
    dominant_pacing: winningPace,
    saturation_risk: saturationRisk,
    strategic_priority: priority,
    confidence: overallConfidence,
    projected_virality: projectedVir,
    projected_replay_rate: projectedReplay,
    status: "proposed",
    rationale: priority,
    locks,
    actions,
  };
}

/**
 * Persist the proposal as a `generation_strategy_cycles` row plus its
 * `generation_strategy_actions`. Optionally activate accepted actions
 * by writing them into `generation_biases`, which the existing
 * generators (GenerateIntelligenceBatchPanel etc.) already consume.
 */
export async function commitStrategy(
  cycle: StrategyCycle,
  status: "accepted" | "partial" | "rejected",
  acceptedActions: StrategyAction[],
): Promise<{ cycleId: string }> {
  const { data: cycleRow, error } = await (supabase as any)
    .from("generation_strategy_cycles")
    .insert({
      cycle_number: cycle.cycle_number,
      dominant_platform: cycle.dominant_platform,
      dominant_archetype: cycle.dominant_archetype,
      dominant_format: cycle.dominant_format,
      dominant_escalation: cycle.dominant_escalation,
      dominant_pacing: cycle.dominant_pacing,
      saturation_risk: cycle.saturation_risk,
      strategic_priority: cycle.strategic_priority,
      confidence: cycle.confidence,
      projected_virality: cycle.projected_virality,
      projected_replay_rate: cycle.projected_replay_rate,
      status,
      locks: cycle.locks,
      rationale: cycle.rationale,
      metadata: { source: "orchestrator" },
    })
    .select("id")
    .single();
  if (error) throw error;
  const cycleId = cycleRow.id as string;

  if (cycle.actions.length > 0) {
    const acceptedSet = new Set(acceptedActions.map((a) => `${a.dimension}::${a.target}`));
    const actionRows = cycle.actions.map((a) => ({
      strategy_cycle_id: cycleId,
      action_type: a.action_type,
      dimension: a.dimension,
      target: a.target,
      adjustment: a.adjustment,
      confidence: a.confidence,
      reasoning: a.reasoning,
      accepted: acceptedSet.has(`${a.dimension}::${a.target}`),
    }));
    await (supabase as any).from("generation_strategy_actions").insert(actionRows);

    // Activate accepted actions as live generation biases
    if (status !== "rejected" && acceptedActions.length > 0) {
      const biasRows = acceptedActions
        .filter((a) => a.dimension !== "platform")
        .map((a) => ({
          dimension: a.dimension,
          key: a.target,
          bias_direction: a.adjustment >= 0 ? "increase" : "decrease",
          weight: Math.min(1.5, Math.abs(a.adjustment) * 5),
          confidence: a.confidence,
          evidence_score: cycle.confidence,
          impact_score: a.adjustment * 100,
          rationale: a.reasoning,
          active: true,
        }));
      if (biasRows.length > 0) {
        await (supabase as any).from("generation_biases").insert(biasRows);
      }
    }
  }

  window.dispatchEvent(new CustomEvent(STRATEGY_ACCEPTED_EVENT, { detail: { cycleId, status } }));
  return { cycleId };
}

export async function fetchLatestStrategy(): Promise<{ cycle: any; actions: any[] } | null> {
  const { data: c } = await (supabase as any)
    .from("generation_strategy_cycles")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(1);
  const cycle = c?.[0];
  if (!cycle) return null;
  const { data: a } = await (supabase as any)
    .from("generation_strategy_actions")
    .select("*")
    .eq("strategy_cycle_id", cycle.id)
    .order("created_at", { ascending: true });
  return { cycle, actions: a ?? [] };
}
